/**
 * Pixel da Meta e Google Analytics (GA4) da Brasil Arena, para os anúncios da plataforma.
 * Só carregam depois do "Aceitar" do aviso de cookies; sem os IDs no .env, nada é carregado.
 * A compra (Purchase) não sai daqui: o servidor avisa quando o pagamento é aprovado.
 * Os marcos do organizador (cadastro, primeiro evento, primeira publicação) saem daqui e do servidor,
 * com o mesmo id: a Meta descarta a cópia.
 */
import {
  AdConversion,
  adConversionCalls,
  AdTrackingPayload,
  buildAdTracking,
} from '@/utils/adTracking';

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;

/** Disparado pelo aviso de cookies ao clicar em "Aceitar". */
export const AD_CONSENT_EVENT = 'brasil-arena:cookie-consent-accepted';

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const hasAdConsent = () => /(?:^|;\s*)cookieConsent=accepted(?:;|$)/.test(document.cookie);

const addScript = (src: string) => {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const loadMeta = (pixelId: string) => {
  if (window.fbq) {
    return;
  }
  // Fila igual à do snippet oficial: as chamadas antes do script carregar não se perdem
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;
  addScript('https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', pixelId);
  // As trocas de página seguintes (SPA) o próprio pixel conta pelo history do navegador
  fbq('track', 'PageView');
};

const loadGoogle = (measurementId: string) => {
  if (window.gtag) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // Trocas de página da SPA: a "medição otimizada" do GA4 conta pelo history
  window.gtag('config', measurementId);
  addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`);
};

/** Carrega os pixels se a pessoa já aceitou os cookies. Pode chamar várias vezes. */
export const initAdPixels = () => {
  if (!hasAdConsent()) {
    return;
  }
  if (META_PIXEL_ID) {
    loadMeta(META_PIXEL_ID);
  }
  if (GA4_MEASUREMENT_ID) {
    loadGoogle(GA4_MEASUREMENT_ID);
  }
};

type AdEvent = { eventId: string; eventName?: string };

/** Abriu a página do evento. */
export const trackEventView = ({ eventId, eventName }: AdEvent) => {
  if (!hasAdConsent()) {
    return;
  }
  // O efeito da página roda antes do efeito do App: garante o pixel carregado
  initAdPixels();
  window.fbq?.('track', 'ViewContent', {
    content_ids: [eventId],
    content_type: 'product',
    ...(eventName ? { content_name: eventName } : {}),
  });
  window.gtag?.('event', 'view_item', {
    items: [{ item_id: eventId, ...(eventName ? { item_name: eventName } : {}) }],
  });
};

/** Abriu o formulário de inscrição. */
export const trackInscriptionStart = ({ eventId, eventName }: AdEvent) => {
  if (!hasAdConsent()) {
    return;
  }
  initAdPixels();
  window.fbq?.('track', 'InitiateCheckout', {
    content_ids: [eventId],
    content_type: 'product',
    ...(eventName ? { content_name: eventName } : {}),
  });
  window.gtag?.('event', 'begin_checkout', {
    items: [{ item_id: eventId, ...(eventName ? { item_name: eventName } : {}) }],
  });
};

/** Vai junto com a criação da inscrição (undefined sem consentimento). */
export const currentAdTracking = (): AdTrackingPayload | undefined =>
  buildAdTracking({
    consent: hasAdConsent(),
    cookieHeader: document.cookie,
    search: window.location.search,
    href: window.location.href,
    ga4MeasurementId: GA4_MEASUREMENT_ID,
    now: Date.now(),
  });

/** Cookies de anúncio para mandar junto de um pedido à API (objeto vazio sem consentimento). */
export const withAdTracking = () => {
  const adTracking = currentAdTracking();
  return adTracking ? { adTracking } : {};
};

/** O servidor confirmou um marco do organizador: dispara no pixel e no GA4 (só com consentimento). */
export const trackAdConversion = (conversion?: AdConversion) => {
  if (!conversion || !hasAdConsent()) {
    return;
  }
  initAdPixels();
  const calls = adConversionCalls(conversion);
  window.fbq?.(...calls.fbq);
  window.gtag?.(...calls.gtag);
};
